import type { SelectedAncillaryPassenger } from './Ancillaries';
import type { PassengerSeatingPayload, PassengerType } from './Passenger';
import type { IATACode, ISODate } from './Common';
import type { PricingResponse } from './Price';
import type { Station, Trip } from './Trip';

// ===== Payload types ===== //

export interface TripSearchPayload {
  outbound: TripSearchLegPayload;
  inbound?: TripSearchLegPayload;
  passengers: PassengerSeatingPayload[];
}

interface TripSearchLegPayload {
  origin: Pick<Station, 'code' | 'type'>;
  destination: Pick<Station, 'code' | 'type'>;
  date: string;
}

// ===== Response types ===== //

export interface TripSearchResponse {
  id: string;
  outbound: TripResult[];
  inbound?: TripResult[];
  ancillaries?: SelectedAncillaryPassenger[];
}

/** Journey from GET /availabilities: cached, with no per-passenger prices. */
export interface BrowsedTripResult extends Optional<Trip, 'fare' | 'serviceNumber'> {
  id: string;
  availability: Availability;
  pricing: Omit<PricingResponse, 'passengerPrices'>;
}

/** Journey from POST /availabilities: live, priced per passenger. */
export interface TripResult extends BrowsedTripResult {
  pricing: PricingResponse;
}

export interface Availability {
  status: 'AVAILABLE' | 'UNAVAILABLE' | 'LIMITED' | 'SOLD_OUT' | 'CANCELLED';
  seatsRemaining?: number;
}

// ===== Availability & trip search parameters ===== //

export interface TripSearchParametersResponse {
  routes: AvailabilityParamsResponse[];
}

export type AvailabilityParamsResponse = Origin & {
  origin: IATACode;
  destinations: Destination[];
};

export type Origin = StationItem;
export type Destination = Prettify<DestinationBase & StationItem>;

interface DestinationBase {
  destination: IATACode;
  tags: string[];
  bookingWindow: BookingWindow;
}

export interface BookingWindow {
  startDate: ISODate;
  endDate: ISODate;
}

export type StationItem = Prettify<SingleItem | GroupItem>;

export interface SingleItem {
  /** Resolved station type of the destination — any type except `STATION_GROUP`. */
  type?: Exclude<StationType, 'STATION_GROUP'>;
}

export interface GroupItem {
  /** Resolved station type of the destination. Always `STATION_GROUP` for a group. */
  type?: 'STATION_GROUP';
  /** Sub-classification when type is STATION_GROUP. */
  groupType?: StationGroupType;
  /** The list of stations that this custom group contains */
  stations?: Station[];
}

/** Kind of station resolved by the backend (airport vs. tenant group). */
export type StationType = 'AIRPORT' | 'STATION_GROUP';

/** Sub-classification of a STATION_GROUP: operational MAC code, tenant custom group, or the ANYWHERE sentinel. */
export type StationGroupType = 'MAC' | 'CUSTOM_GROUP' | 'ANYWHERE';
export const ANYWHERE_GROUP_CODE = 'ANYWHERE';

// ===== Availability browse (GET) ===== //

export interface AvailabilitySearchResponse {
  origin: Pick<Station, 'code' | 'type'>;
  destination: Pick<Station, 'code' | 'type'>;
  availabilityByDate: AvailabilityByDate[];
}

export interface AvailabilityByDate {
  date: ISODate;
  journeys: BrowsedTripResult[];
}

export interface AvailabilityBrowseParams {
  origin: string;
  destination: string;
  from: ISODate;
  to: ISODate;
  [key: string]: string;
}

/** The maximum date range the endpoint supports. */
export const BROWSE_MAX_WINDOW_DAYS = 7;

/**
 * Raw response of the availability-params/groups endpoint: a flat array of origin groups
 * (unlike availability-params, which wraps the same per-route shape in a `routes` envelope).
 * Each entry carries resolved station types and supports GROUP-type destinations with their
 * own booking window.
 */
export type AvailabilityGroupsResponse = AvailabilityParamsResponse[];

// ===== Search analytics ===== //

/** Metadata passed as the `Search-Analytics` header on the availability POST request. */
export interface SearchAnalytics {
  /** Groups every availability call of one search. Never carries user or tenant data. */
  searchId?: string;
}

/** Metadata passed as the `Search-Analytics` header on the availability GET request. */
export interface SearchAnalyticsMetadata extends SearchAnalytics {
  tripType: 'OW' | 'RT';
  departureDate: ISODate;
  /** Round trips only, once a return date is chosen. */
  returnDate?: ISODate;
  /** Passengers for the search, counted by type. Left out of the header when empty. */
  passengers?: PassengerCount[];
}

export interface PassengerCount {
  type: PassengerType;
  count: number;
}
